import { useAskThreads, useDeleteAskThread, useRenameAskThread } from "@nook/api-client"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  toast,
} from "@nook/ui"
import { Link, useNavigate, useParams } from "@tanstack/react-router"
import { MoreHorizontal, Pencil, Trash2 } from "lucide-react"
import { useEffect, useRef, useState } from "react"

/**
 * Ask thread history (block 42), in the sidebar on every page. Renamed in
 * place from the row's `⋯`.
 */
export function AskThreads() {
  const threads = useAskThreads()
  const remove = useDeleteAskThread()
  const navigate = useNavigate()
  const { threadId: current } = useParams({ strict: false }) as { threadId?: string }
  const [deleting, setDeleting] = useState<string | null>(null)
  const [renaming, setRenaming] = useState<string | null>(null)
  const rename = useRenameAskThread()
  // Radix returns focus to the ⋯ trigger on close, which would blur the rename input at once.
  const keepFocus = useRef(false)

  const commit = (id: string, before: string | null, value: string) => {
    setRenaming(null)
    const title = value.trim()
    if (title && title !== before) rename.mutate({ id, title }, { onError: (error) => toast.error(error.message) })
  }
  const sentinel = useRef<HTMLDivElement>(null)
  const items = threads.data?.pages.flatMap((page) => page.items) ?? []
  const { hasNextPage, isFetching, fetchNextPage } = threads

  // The same infinite scroll as the library, and not mid-fetch for the same reason.
  useEffect(() => {
    const target = sentinel.current
    if (!target || !hasNextPage || isFetching) return

    const observer = new IntersectionObserver(
      ([entry]) => entry?.isIntersecting && void fetchNextPage(),
      {
        rootMargin: "200px",
      },
    )
    observer.observe(target)
    return () => observer.disconnect()
  }, [hasNextPage, isFetching, fetchNextPage])

  function confirm() {
    const id = deleting!
    setDeleting(null)
    remove.mutate(id, { onError: (error) => toast.error(error.message) })

    // Before the transcript's query is dropped, so the open thread never flashes "not available".
    if (id === current) {
      const next = items.find((thread) => thread.id !== id)
      void navigate({
        to: "/ask/$threadId",
        params: { threadId: next?.id ?? "new" },
        replace: true,
      })
    }
  }

  if (threads.isSuccess && items.length === 0) return null

  return (
    <SidebarGroup className="p-0 pt-4 group-data-[collapsible=icon]:hidden">
      <SidebarGroupLabel className="text-[0.6875rem] font-medium tracking-wide uppercase">
        Threads
      </SidebarGroupLabel>

      <SidebarMenu className="gap-0.5">
        {threads.isPending
          ? Array.from({ length: 3 }, (_, index) => <SidebarMenuSkeleton key={index} />)
          : null}
        {threads.isError ? (
          <p className="text-muted-foreground px-2 text-sm">
            Could not load your threads.
          </p>
        ) : null}

        {items.map((thread) => (
          <SidebarMenuItem key={thread.id}>
            {renaming === thread.id ? (
              <Input
                autoFocus
                aria-label="Thread name"
                defaultValue={thread.title ?? ""}
                maxLength={60}
                className="h-8 text-[0.8125rem]"
                onFocus={(event) => event.currentTarget.select()}
                onBlur={(event) => commit(thread.id, thread.title, event.currentTarget.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") event.currentTarget.blur()
                  if (event.key === "Escape") {
                    event.currentTarget.value = thread.title ?? ""
                    event.currentTarget.blur()
                  }
                }}
              />
            ) : (
              <SidebarMenuButton
                asChild
                isActive={thread.id === current}
                className="text-muted-foreground data-[active=true]:text-foreground text-[0.8125rem]"
              >
                <Link to="/ask/$threadId" params={{ threadId: thread.id }} title={thread.title ?? undefined}>
                  <span>{thread.title ?? "Untitled"}</span>
                </Link>
              </SidebarMenuButton>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuAction showOnHover aria-label="Thread actions">
                  <MoreHorizontal />
                </SidebarMenuAction>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side="right"
                align="start"
                onCloseAutoFocus={(event) => {
                  if (keepFocus.current) event.preventDefault()
                  keepFocus.current = false
                }}
              >
                <DropdownMenuItem
                  onSelect={() => {
                    keepFocus.current = true
                    setRenaming(thread.id)
                  }}
                >
                  <Pencil />
                  Rename
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  variant="destructive"
                  onSelect={() => setDeleting(thread.id)}
                >
                  <Trash2 />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        ))}

        <div ref={sentinel}>
          {threads.isFetchingNextPage ? <SidebarMenuSkeleton /> : null}
        </div>
      </SidebarMenu>

      <AlertDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this thread?</AlertDialogTitle>
            <AlertDialogDescription>
              The questions and answers in it are removed permanently. This cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={confirm}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SidebarGroup>
  )
}
